import { ScrollArea } from './ScrollArea';
import { useInfiniteScroll } from '@ais-ui/custom-hooks';
import { useMemo, useCallback } from 'react';
import React from 'react';

type ScrollableListProps<T> = {
  items: T[] | undefined | null;
  renderItem: (item: T) => React.ReactNode;
  limit: number;
  observeResize: boolean;
  className?: string;
  onScrollViewportRef?: (ref: HTMLDivElement | null) => void; // Add this prop

  /**
   * Custom filter function for searching
   */
  filterFn?: (item: T, search: string) => boolean;

  /**
   * Optional sorting function
   */
  sortFn?: (a: T, b: T) => number;
};

export const InfiniteScroll = <T,>({
  items,
  renderItem,
  limit,
  observeResize,
  className,
  onScrollViewportRef,
  filterFn,
  sortFn,
  search,
}: ScrollableListProps<T> & { search?: string }) => {
  const { viewportRef, offset } = useInfiniteScroll(limit);

  const visibleItems = useMemo(() => {
    if (!items) return [];

    let result = [...items];

    if (filterFn && search) {
      result = result.filter((item) => filterFn(item, search));
    }

    if (sortFn) {
      result.sort(sortFn);
    }

    return result.slice(0, offset + limit);
  }, [items, search, offset, limit, filterFn, sortFn]);

  // Get the viewport ref from ScrollArea and pass it to parent
  const scrollAreaRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && onScrollViewportRef) {
        // ShadCN ScrollArea creates a viewport div inside
        const viewport = node.querySelector(
          '[data-radix-scroll-area-viewport]'
        ) as HTMLDivElement;
        onScrollViewportRef(viewport);
      }
    },
    [onScrollViewportRef]
  );

  return (
    <ScrollArea
      ref={scrollAreaRef}
      infiniteScrollRef={viewportRef}
      className={className}
      observeResize={observeResize}
    >
      {visibleItems.map((item, index) => (
        <React.Fragment key={index}>{renderItem(item)}</React.Fragment>
      ))}
    </ScrollArea>
  );
};
